(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SlArtifactCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-sl-artifact-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-sl-artifact-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-sl-artifact-dot]'));
      this.previousButton = root.querySelector('[data-sl-artifact-prev]');
      this.nextButton = root.querySelector('[data-sl-artifact-next]');
      this.counter = root.parentElement.querySelector('[data-sl-artifact-count]');
      this.activeIndex = 0;
      this.pointerStart = null;
      this.autoplayTimer = null;
      this.autoplayEnabled = root.dataset.autoplay === 'true' && !prefersReducedMotion();
      this.autoplayDelay = Math.max(Number(root.dataset.autoplayDelay || 5000), 3000);

      if (!this.stage || this.slides.length < 1) return;

      this.bindEvents();
      this.setActive(0);
      this.startAutoplay();
    }

    normalize(index) {
      const count = this.slides.length;
      return ((index % count) + count) % count;
    }

    shortestOffset(index) {
      const count = this.slides.length;
      let offset = index - this.activeIndex;

      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;

      if (count === 2 && Math.abs(offset) === 1) {
        offset = index > this.activeIndex ? 1 : -1;
      }

      return offset;
    }

    visualState(offset) {
      const direction = Math.sign(offset) || 1;
      const magnitude = Math.abs(offset);

      if (magnitude === 0) {
        return { x: '0%', z: '2rem', rotate: '0deg', scale: '1', opacity: '1', zIndex: '6', filter: 'none' };
      }

      if (magnitude === 1) {
        return {
          x: `${direction * 35}%`,
          z: '-9rem',
          rotate: `${direction * -32}deg`,
          scale: '0.82',
          opacity: '0.68',
          zIndex: '4',
          filter: 'brightness(0.76) saturate(0.82)'
        };
      }

      return {
        x: `${direction * 58}%`,
        z: '-19rem',
        rotate: `${direction * -50}deg`,
        scale: '0.58',
        opacity: '0.08',
        zIndex: '1',
        filter: 'brightness(0.58) saturate(0.58)'
      };
    }

    setActive(index) {
      this.activeIndex = this.normalize(index);

      this.slides.forEach((slide, slideIndex) => {
        const offset = this.shortestOffset(slideIndex);
        const state = this.visualState(offset);
        const isActive = offset === 0;

        slide.style.setProperty('--sl-carousel-x', state.x);
        slide.style.setProperty('--sl-carousel-z', state.z);
        slide.style.setProperty('--sl-carousel-rotate', state.rotate);
        slide.style.setProperty('--sl-carousel-scale', state.scale);
        slide.style.opacity = state.opacity;
        slide.style.zIndex = state.zIndex;
        slide.style.filter = state.filter;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.tabIndex = isActive ? 0 : -1;
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.activeIndex;
        dot.setAttribute('aria-current', String(isActive));
        dot.tabIndex = isActive ? 0 : -1;
      });

      if (this.counter) {
        const current = String(this.activeIndex + 1).padStart(2, '0');
        const total = String(this.slides.length).padStart(2, '0');
        this.counter.textContent = `${current} / ${total}`;
      }
    }

    goNext() {
      this.setActive(this.activeIndex + 1);
    }

    goPrevious() {
      this.setActive(this.activeIndex - 1);
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.goPrevious());
      this.nextButton?.addEventListener('click', () => this.goNext());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.setActive(index));
      });

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', () => {
          if (index !== this.activeIndex) this.setActive(index);
        });
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goNext();
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goPrevious();
        }
      });

      this.stage.addEventListener('pointerdown', (event) => {
        this.pointerStart = { x: event.clientX, id: event.pointerId };
        this.stage.setPointerCapture?.(event.pointerId);
      });

      this.stage.addEventListener('pointerup', (event) => {
        if (!this.pointerStart) return;

        const distance = event.clientX - this.pointerStart.x;
        if (Math.abs(distance) > 42) {
          distance < 0 ? this.goNext() : this.goPrevious();
        }

        this.pointerStart = null;
      });

      this.stage.addEventListener('pointercancel', () => {
        this.pointerStart = null;
      });

      this.root.addEventListener('pointerenter', () => this.stopAutoplay());
      this.root.addEventListener('pointerleave', () => this.startAutoplay());
      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', () => this.startAutoplay());
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.slides.length < 2 || this.autoplayTimer) return;
      this.autoplayTimer = window.setInterval(() => this.goNext(), this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-sl-artifact-carousel]').forEach((root) => {
      if (root.dataset.slArtifactCarouselReady === 'true') return;
      root.dataset.slArtifactCarouselReady = 'true';
      new SlArtifactCarousel(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  initialize();
})();
